import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { ScrollToTop } from '@/app/ScrollToTop.js';
import { Layout } from '@/shell/Layout.js';
import { Account, AccountProvider, VerificationGate } from '@/features/account/index.js';
import {
  CHINA_PAYMENT_TYPE_SLUGS,
  ChinaPayment,
  TopUpRecords,
} from '@/features/china-payment/index.js';
import { Coupons, CouponsProvider } from '@/features/coupons/index.js';
import { NotificationsProvider } from '@/features/notifications/index.js';
import {
  ACCOUNT_MENU,
  ACCOUNT_PROFILE_ROUTE,
  ACCOUNT_SECTION_REDIRECTS,
  CHINA_PAYMENT_RECORDS_ROUTE,
  CHINA_PAYMENT_ROUTE,
  COUPONS_ROUTE,
  EDIT_ORDER_ROUTE,
  LEGACY_CHINA_PAYMENT_ROUTES,
  NAV,
  NEW_ORDER_ROUTE,
  NOTIFICATIONS_ROUTE,
  ORDERS_ROUTE,
  PACKAGES_ROUTE,
  WALLET_BALANCE_ROUTE,
  WALLET_TOP_UP_RESULT_ROUTE,
  WAREHOUSES_ROUTE,
  type AccountMenuItem,
  type NavChild,
} from '@/shell/navigation.js';
import { Dashboard } from '@/features/dashboard/index.js';
import { NotFound } from '@/pages/NotFound.js';
import { Notifications } from '@/features/notifications/index.js';
import { Packages, PackagesProvider } from '@/features/packages/index.js';
import { Placeholder } from '@/pages/Placeholder.js';
import { Settings } from '@/pages/Settings.js';
import {
  CreateOrder,
  EditOrder,
  OrderCatalogProvider,
  OrderServicesProvider,
  Orders,
  OrdersProvider,
} from '@/features/orders/index.js';
import { ToastProvider } from '@/features/toast/index.js';
import { TopUpResult, Wallet, WalletProvider } from '@/features/wallet/index.js';
import { Warehouses, WarehousesProvider } from '@/features/warehouses/index.js';

/** Children that have a real page of their own, and so are not given a placeholder. */
const REAL_ROUTES = new Set<string>([
  NOTIFICATIONS_ROUTE,
  WAREHOUSES_ROUTE,
  ORDERS_ROUTE,
  PACKAGES_ROUTE,
  COUPONS_ROUTE,
  ACCOUNT_PROFILE_ROUTE,
  WALLET_BALANCE_ROUTE,
  CHINA_PAYMENT_ROUTE,
  CHINA_PAYMENT_RECORDS_ROUTE,
]);

/**
 * Every sidebar child gets a route, generated from NAV so the menu can never point at a path that
 * doesn't exist. Children without a real page yet render the placeholder.
 */
const placeholderRoutes: { to: string; key: NavChild['key'] | AccountMenuItem['key'] }[] = [
  ...NAV.flatMap((item): NavChild[] => ('children' in item ? [...item.children] : [])),
  // The account menu's pages (MYP-136) are not in the sidebar, but get a route the same way.
  ...ACCOUNT_MENU,
].filter((child) => !REAL_ROUTES.has(child.to));

/**
 * Each sidebar section's own path, sent to its first child (`/international` →
 * `/international/packages`), plus the old section paths of the pages that moved to the profile
 * menu (`/wallet`, `/coupons`, `/warehouse-address`, MYP-136).
 */
const sectionRedirects = [
  ...NAV.flatMap((item): { from: string; to: string }[] =>
    'children' in item && item.children[0] ? [{ from: item.to, to: item.children[0].to }] : [],
  ),
  ...ACCOUNT_SECTION_REDIRECTS,
];

export function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <ToastProvider>
        <NotificationsProvider>
          {/*
            The session (Account) sits above Wallet: the wallet's balance, currency and payment mode
            are seeded from it, and a provider can only read one above it.
          */}
          <AccountProvider>
            <WalletProvider>
              <CouponsProvider>
                <OrdersProvider>
                  <WarehousesProvider>
                    <PackagesProvider>
                      {/*
                        `order_services` (28 rows) is small enough to fetch app-wide, the same tier
                        as Warehouses/Packages above it — `OrderDetailPanel` on the Orders *list*
                        resolves a service id to its name (MYP-53 slice 2) and has no route of its
                        own to wrap. `OrderCatalogProvider` below is the bigger reference data
                        (205 channels, the country/province/city cascade) and stays scoped to just
                        the two order-editing routes per the Jira ticket, so an Orders-list visitor
                        never pays for it.
                      */}
                      <OrderServicesProvider>
                        <Layout>
                          <Routes>
                            {/* No landing page of its own: the site opens straight into the Dashboard. */}
                            <Route path="/" element={<Navigate to="/dashboard" replace />} />
                            <Route path="/dashboard" element={<Dashboard />} />
                            <Route path={NOTIFICATIONS_ROUTE} element={<Notifications />} />
                            <Route path={WAREHOUSES_ROUTE} element={<Warehouses />} />
                            <Route path={PACKAGES_ROUTE} element={<Packages />} />
                            <Route path={COUPONS_ROUTE} element={<Coupons />} />
                            <Route path={ACCOUNT_PROFILE_ROUTE} element={<Account />} />
                            <Route path={WALLET_BALANCE_ROUTE} element={<Wallet />} />
                            <Route path={WALLET_TOP_UP_RESULT_ROUTE} element={<TopUpResult />} />
                            <Route path={CHINA_PAYMENT_ROUTE} element={<ChinaPayment />} />
                            <Route path={CHINA_PAYMENT_RECORDS_ROUTE} element={<TopUpRecords />} />
                            {/* The three pages it replaced, kept so old links land on their tab. */}
                            {(['topUp', 'proxyPayment', 'transfer'] as const).map((page) => (
                              <Route
                                key={page}
                                path={LEGACY_CHINA_PAYMENT_ROUTES[page]}
                                element={
                                  <Navigate
                                    to={`${CHINA_PAYMENT_ROUTE}?type=${CHINA_PAYMENT_TYPE_SLUGS[page]}`}
                                    replace
                                  />
                                }
                              />
                            ))}
                            <Route path={ORDERS_ROUTE} element={<Orders />} />
                            <Route
                              path={NEW_ORDER_ROUTE}
                              element={
                                <VerificationGate>
                                  <OrderCatalogProvider>
                                    <CreateOrder />
                                  </OrderCatalogProvider>
                                </VerificationGate>
                              }
                            />
                            <Route
                              path={EDIT_ORDER_ROUTE}
                              element={
                                <OrderCatalogProvider>
                                  <EditOrder />
                                </OrderCatalogProvider>
                              }
                            />

                            {/*
                              A section path is not a page, but typing or bookmarking one should not
                              land on NotFound: each goes to its section's first screen, read from
                              NAV so a new section is covered too. `/account` is not a sidebar
                              section, so it is listed on its own.
                            */}
                            {sectionRedirects.map(({ from, to }) => (
                              <Route
                                key={from}
                                path={from}
                                element={<Navigate to={to} replace />}
                              />
                            ))}
                            <Route
                              path="/account"
                              element={<Navigate to={ACCOUNT_PROFILE_ROUTE} replace />}
                            />
                            <Route path="/settings" element={<Settings />} />

                            {placeholderRoutes.map((child) => (
                              <Route
                                key={child.to}
                                path={child.to}
                                element={<Placeholder titleKey={child.key} />}
                              />
                            ))}

                            <Route path="*" element={<NotFound />} />
                          </Routes>
                        </Layout>
                      </OrderServicesProvider>
                    </PackagesProvider>
                  </WarehousesProvider>
                </OrdersProvider>
              </CouponsProvider>
            </WalletProvider>
          </AccountProvider>
        </NotificationsProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}
